import apiClient from "../lib/api";

export interface RagCollection {
	id: string;
	routeConfigId: string;
	qdrantCollection: string;
	chunkSize: number;
	chunkOverlap: number;
	topK: number;
	similarityThreshold: number;
	documentCount: number;
	totalChunks: number;
	totalSizeBytes: string;
	createdAt: string;
	updatedAt: string;
}

export interface RagDocument {
	id: string;
	collectionId: string;
	filename: string;
	mimeType: string;
	sizeBytes: string;
	storagePath: string;
	status: "pending" | "processing" | "completed" | "failed";
	chunkCount: number;
	errorMessage: string | null;
	metadata: Record<string, unknown> | null;
	createdAt: string;
	processedAt: string | null;
}

export interface DocumentListResponse {
	documents: RagDocument[];
	total: number;
}

export interface UpdateCollectionSettings {
	chunkSize?: number;
	chunkOverlap?: number;
	topK?: number;
	similarityThreshold?: number;
	embeddingModel?: string | null;
	embeddingProvider?: string | null;
}

/**
 * RAG Service
 * API client for RAG collection and document management
 */
export const ragService = {
	/**
	 * Get collection info
	 */
	async getCollection(routeConfigId: string): Promise<RagCollection> {
		const response = await apiClient.get(`/rag/collections/${routeConfigId}`);
		return response.data;
	},

	/**
	 * List documents in collection
	 */
	async listDocuments(routeConfigId: string): Promise<DocumentListResponse> {
		const response = await apiClient.get(
			`/rag/collections/${routeConfigId}/documents`,
		);
		return response.data;
	},

	/**
	 * Upload document
	 */
	async uploadDocument(
		routeConfigId: string,
		file: File,
		onProgress?: (progress: number) => void,
	): Promise<RagDocument> {
		const formData = new FormData();
		formData.append("file", file);

		const response = await apiClient.post(
			`/rag/collections/${routeConfigId}/documents`,
			formData,
			{
				headers: {
					"Content-Type": "multipart/form-data",
				},
				onUploadProgress: (progressEvent) => {
					if (onProgress && progressEvent.total) {
						const progress = Math.round(
							(progressEvent.loaded * 100) / progressEvent.total,
						);
						onProgress(progress);
					}
				},
			},
		);

		return response.data;
	},

	/**
	 * Get document by ID
	 */
	async getDocument(documentId: string): Promise<RagDocument> {
		const response = await apiClient.get(`/rag/documents/${documentId}`);
		return response.data;
	},

	/**
	 * Delete document
	 */
	async deleteDocument(documentId: string): Promise<void> {
		await apiClient.delete(`/rag/documents/${documentId}`);
	},

	/**
	 * Reprocess document
	 */
	async reprocessDocument(documentId: string): Promise<{ success: boolean }> {
		const response = await apiClient.post(
			`/rag/documents/${documentId}/reprocess`,
		);
		return response.data;
	},

	/**
	 * Update collection settings
	 */
	async updateCollectionSettings(
		routeConfigId: string,
		settings: UpdateCollectionSettings,
	): Promise<RagCollection> {
		const response = await apiClient.patch(
			`/rag/collections/${routeConfigId}`,
			settings,
		);
		return response.data;
	},

	/**
	 * Delete collection
	 */
	async deleteCollection(routeConfigId: string): Promise<void> {
		await apiClient.delete(`/rag/collections/${routeConfigId}`);
	},
};
